import _                                              from 'underscore';
import React, { FC, ReactNode }                       from 'react';
import { Select, SelectProps }                        from 'semantic-ui-react';
import { useField, useFormikContext }                 from 'formik';
import { FieldWrapper }                               from './FieldWrapper';
import { FieldDescription }                           from './FieldDescription';
import { formatOption }                               from '../../../../util';
import { SELECTFIELD_EMPTY_VALUE }                    from '../../../../constants/FormEnums';
import { FormikUseFieldType, PreSelectedSelectField } from '../../../../types';

export interface SelectFieldProps extends SelectProps {
	options: SelectProps['options'] & { group?: string },
	label?: string | ReactNode,
	descriptionText?: string,
	includeFieldWrapper?: boolean,
	includeErrorMessage?: boolean,

	/* Flag to include a space dummy value in first option position */
	preventFirstSelection?: boolean,

	/* If included, and options have group attribute, present in grouped display */
	useGroupNames?: boolean,

	hideOnEmpty?: boolean,
	disableOnEmpty?: boolean,

	/* Values to omit from options set */
	omit?: ( string | number )[],

	/* Values to pluck from options set */
	only?: ( string | number )[],

	/* Values to append to end of options set */
	append?: ( string | number )[],

	/* Values to prepend at the start of options set */
	prepend?: ( string | number )[],

	/* Map that converts option labels based on their value; useful for similar
	dropdowns that present similar values in different language contexts */
	dictionary?: Map<string | number, string | number | { value: string, text: string }>

	onChange?( value: any ): void

	onBlur?( value: any ): void

	//[ additionalProps: string ]: any
}

export type QueriedSelectFieldProps = PreSelectedSelectField;

const defaultProps: SelectFieldProps = {
	options:               [],
	label:                 null,
	descriptionText:       null,
	includeFieldWrapper:   true,
	includeErrorMessage:   true,
	preventFirstSelection: true,
	useGroupNames:         false,
	hideOnEmpty:           false,
	disableOnEmpty:        false,
	omit:                  null,
	only:                  null,
	append:                null,
	prepend:               null,
	dictionary:            null,
	onChange:              null,
	onBlur:                null,
}

const SelectField: FC<SelectFieldProps> = ( _props ) => {

	let {
		    label,
		    descriptionText,
		    includeFieldWrapper,
		    includeErrorMessage,
		    preventFirstSelection,
		    useGroupNames,
		    hideOnEmpty,
		    disableOnEmpty,
		    omit,
		    only,
		    append,
		    prepend,
		    dictionary,
		    ...props
	    } = _props;

	// useField() returns [formik.getFieldProps(), formik.getFieldMeta()]
	// which we can spread on <input> and also replace ErrorMessage entirely.
	let formik = useFormikContext();

	const [ field, meta, helpers ] = (
		formik
		? useField( props.name )
		: [ { name: props.name, value: props.value || null, }, {}, {} ]
	) as FormikUseFieldType;

	let showErrors  = ( meta.touched && meta.error ),
	    Wrapper: FC = includeFieldWrapper ? FieldWrapper : React.Fragment;

	let options = props.options;

	// omit any values
	if( _.isArray( omit ) ){
		options = _.reject( options, option => _.contains( omit, option.value ) );
	}

	// include only values
	if( _.isArray( only ) ){
		options = _.filter( options, option => _.contains( only, option.value ) );
	}

	// append / prepend any values
	if( _.isArray( prepend ) ){
		options = [ ...prepend, ...options ] as SelectProps['options'];
	}
	if( _.isArray( append ) ){
		options = [ ...options, ...append ] as SelectProps['options'];
	}

	// map a dictionary
	// @ts-ignore ( _.isMap is missing )
	if( _.isMap( dictionary ) ){
		options = _.map( options, o => {
			let mappedTerm = dictionary.get( o.value as string | number ),
			    optionText = mappedTerm || o.text;
			return { ...o, text: optionText }
		} );
	}

	// disable on empty option set?
	if( _.isArray( options ) && _.isEmpty( options ) && disableOnEmpty ){
		props.disabled = true
	}

	// show groups
	let groupNames = _.chain( options ).pluck( 'group' ).uniq().compact().value();

	if( useGroupNames && groupNames.length ){

		let groupedOptions = _.groupBy( options, 'group' );

		options = _.flatten( groupNames.map( group => [
			{ ...formatOption( group, group ), disabled: true, className: 'group-label' },
			...groupedOptions[ group ]
		] ) );
	}

	if( preventFirstSelection ){

		options = [
			{ ...formatOption( SELECTFIELD_EMPTY_VALUE, '' ), className: 'display--hidden selector-speedbump', text: <React.Fragment/> },
			...options
		]
	}

	return (

		<Wrapper { ...{ showErrors, includeErrorMessage, meta, field } }>

			{ label && <label htmlFor={ props.id || props.name }>{ label }</label> }

			{ descriptionText && <FieldDescription>{ descriptionText }</FieldDescription> }

			{/* https://marcusmth.com/formik-semantic-ui-2020-01-07/ */ }
			{/* Note: <Select /> is sugar for <Dropdown selection /> */ }
			{ props.loading ? (

				<Select
					disabled={ true }
					{ ...props }
					options={ [ formatOption( 0, 'Loading...' ) ] }
					value={ props.multiple ? [] : 0 }
					placeholder={ 'Loading...' }
				/>

			) : (

				  !disableOnEmpty && (
					  <Select
						  { ...props }
						  options={ options }
						  onChange={ ( e, v ) => {

							  if( formik ) helpers.setValue( v.value );

							  if( _.isFunction( props.onChange ) ){
								  props.onChange( v.value );
							  }
						  } }
						  onBlur={ ( e, v ) => {

							  if( formik ) helpers.setTouched( true );

							  if( _.isFunction( props.onBlur ) ){
								  props.onBlur( v.value );
							  }
						  } }
						  value={ props.multiple ? _.compact( _.flatten( [ field.value ] ) ) : field.value }/>
				  )

			  ) }

		</Wrapper>
	);
};

SelectField.defaultProps = defaultProps;

export { SelectField }
